import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/lib/store";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LayoutDashboard, ShoppingCart, FlaskConical, Boxes, FileText, Users, Building2, Handshake, DollarSign, Newspaper, ShieldCheck, Activity, Settings, Home as HomeIcon, Image, Dumbbell, Salad, ShieldAlert, CreditCard, ClipboardList, Trophy, BookOpen, Layout, Sparkles } from "lucide-react";
import { CrudManager } from "@/components/admin/CrudManager";
import { ChromeEditor } from "@/components/admin/ChromeEditor";
import { HomeEditor } from "@/components/admin/HomeEditor";
import { MediaLibrary } from "@/components/admin/MediaManager";
import { ProductsManager } from "@/components/admin/ProductsManager";
import { ProgramsAdmin } from "@/components/admin/ProgramsAdmin";
import { GuideEditor } from "@/components/admin/GuideEditor";
import VRLab from "@/components/admin/VRLab";
import InvoiceAdmin from "@/components/admin/InvoiceAdmin";
import OrdersManager from "@/components/admin/OrdersManager";
import UsersManager from "@/components/admin/UsersManager";

const GROUPS = [
  ["Overview", [["dashboard", "Dashboard", LayoutDashboard]]],
  ["Appearance", [["home", "Home Page", HomeIcon], ["chrome", "Header & Footer", Layout], ["media", "Media Library", Image]]],
  ["Catalog", [["products", "Products", Boxes], ["peptides", "Peptides", FlaskConical], ["guides", "Guide Editor", BookOpen], ["coas", "COAs", FileText]]],
  ["Content", [["articles", "Journal / CMS", Newspaper]]],
  ["VR Lab", [["vrlab", "VR Lab", Sparkles]]],
  ["Payments", [["invoicing", "Invoicing", CreditCard]]],
  ["Programs", [["prog_overview", "Programs", Activity], ["prog_users", "Members & Editor", Users], ["membership_plans", "Memberships", CreditCard], ["access_rules", "Access Rules", ClipboardList], ["safety_rules", "Safety Rules", ShieldAlert], ["meals", "Meals", Salad], ["exercises", "Exercises", Dumbbell], ["program_tips", "Daily Tips", Trophy], ["program_questions", "Questionnaire", ClipboardList], ["prog_audit", "Audit Log", FileText]]],
  ["Operations", [["orders", "Orders", ShoppingCart], ["wholesale_apps", "Wholesale", Building2], ["partner_apps", "Partners", Handshake], ["commissions", "Commissions", DollarSign], ["tickets", "Support", Activity]]],
  ["System", [["users", "Users", Users], ["verify_scans", "Compliance Scans", ShieldCheck], ["settings", "Settings", Settings]]],
];

const CRUD = new Set(["peptides", "coas", "articles", "meals", "exercises", "program_tips", "access_rules", "safety_rules", "membership_plans", "program_questions"]);
const READONLY = ["wholesale_apps", "partner_apps", "commissions", "tickets", "verify_scans"];

const Dashboard = ({ stats }) => (
  <div>
    <h1 className="font-serif text-4xl text-white mb-8">Overview</h1>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {[["Revenue", `$${stats.revenue.toFixed(2)}`], ["Orders", stats.orders], ["AOV", `$${stats.aov.toFixed(2)}`], ["Customers", stats.customers],
        ["Wholesale Pending", stats.wholesale_pending], ["Partner Pending", stats.partner_pending], ["Peptides", stats.peptides], ["Products", stats.products],
        ["COAs", stats.coas], ["Verify Scans", stats.verify_scans], ["Open Tickets", stats.tickets]].map(([l, v]) => (
        <div key={l} className="glass rounded-2xl p-5">
          <div className="font-mono text-2xl text-cyan-300">{v}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">{l}</div>
        </div>
      ))}
    </div>
  </div>
);

const ReadOnlyTable = ({ section }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const { impersonate } = useApp();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/data/${section}`); setRows(data); } catch { setRows([]); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [section]);

  const approve = async (type, id) => {
    try { await api.post(`/admin/${type}/${id}/approve`); toast.success("Approved"); load(); } catch { toast.error("Failed"); }
  };

  const swap = async (id) => {
    try { navigate("/my-lab"); await impersonate(id); toast.success("Now viewing as this user"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not swap"); }
  };

  const cols = rows.length ? Object.keys(rows[0]).filter((k) => !["_id", "password_hash", "body", "description", "overview", "items", "shipping"].includes(k)).slice(0, 7) : [];

  return (
    <div>
      <h1 className="font-serif text-4xl text-white mb-6 capitalize">{section.replace(/_/g, " ")}</h1>
      {loading && <div className="text-slate-500 font-mono text-sm animate-pulse">Loading…</div>}
      {!loading && rows.length === 0 && <div className="glass rounded-2xl p-10 text-center text-slate-500 text-sm">No records.</div>}
      {!loading && rows.length > 0 && (
        <div className="glass rounded-2xl overflow-hidden"><div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/10">
              {cols.map((c) => <th key={c} className="p-3 text-left text-[10px] font-mono uppercase tracking-widest text-slate-500">{c}</th>)}
              <th className="p-3" />
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-row-${r.id}`}>
                  {cols.map((c) => (
                    <td key={c} className="p-3 font-mono text-xs text-slate-300 max-w-[180px] truncate">
                      {typeof r[c] === "object" ? JSON.stringify(r[c]).slice(0, 40) : String(r[c] ?? "—").slice(0, 40)}
                    </td>
                  ))}
                  <td className="p-3">
                    {section === "wholesale_apps" && r.status === "under_review" && <Button size="sm" onClick={() => approve("wholesale", r.id)} data-testid={`approve-wholesale-${r.id}`} className="btn-primary rounded-lg text-xs">Approve</Button>}
                    {section === "partner_apps" && r.status === "pending" && <Button size="sm" onClick={() => approve("partner", r.id)} data-testid={`approve-partner-${r.id}`} className="btn-primary rounded-lg text-xs">Approve</Button>}
                    {section === "commissions" && r.status === "pending" && <Button size="sm" onClick={() => approve("commission", r.id)} data-testid={`approve-commission-${r.id}`} className="btn-primary rounded-lg text-xs">Approve</Button>}
                    {section === "users" && !["super_admin", "admin", "manager"].includes(r.role) && (
                      <Button size="sm" onClick={() => swap(r.id)} data-testid={`swap-user-${r.id}`} className="btn-ghost rounded-lg text-xs">Swap as user</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
      )}
    </div>
  );
};

const SettingsView = () => (
  <div className="max-w-2xl">
    <h1 className="font-serif text-4xl text-white mb-6">Settings & Integrations</h1>
    <div className="space-y-4">
      {[["Payments", "Stripe checkout architecture. Configure merchant-approved providers before going live.", "NOT CONNECTED"],
        ["Email", "Resend-managed transactional email architecture.", "READY"],
        ["AI Assistant", "Claude Sonnet 5 via Emergent Universal Key. Ask CG Labz is live.", "CONNECTED"],
        ["Media Storage", "Emergent object storage — admin uploads persist across deploys.", "CONNECTED"],
        ["Compliance", "Product status: SELLABLE / CATALOG ONLY / RESEARCH ONLY / REGION RESTRICTED / DISABLED.", "ACTIVE"]].map(([t, d, st]) => (
        <div key={t} className="glass rounded-2xl p-5 flex items-start justify-between gap-4">
          <div><div className="text-sm font-medium text-white">{t}</div><div className="text-xs text-slate-400 mt-1">{d}</div></div>
          <span className={`stat-pill shrink-0 ${["CONNECTED", "ACTIVE", "READY"].includes(st) ? "" : "opacity-50"}`}>{st}</span>
        </div>
      ))}
    </div>
  </div>
);

export default function Admin() {
  const [section, setSection] = useState("dashboard");
  const [stats, setStats] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, [section]);

  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  return (
    <div className="pt-16 min-h-screen flex" data-testid="admin-dashboard">
      <aside className="w-60 shrink-0 border-r border-white/10 bg-black/40 hidden md:block overflow-y-auto" data-testid="admin-sidebar">
        <div className="p-5 border-b border-white/10">
          <div className="font-serif text-xl">CG Labz</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 mt-1">Admin Console</div>
        </div>
        <nav className="p-3 space-y-4">
          {GROUPS.map(([group, items]) => (
            <div key={group}>
              <div className="text-[9px] font-mono uppercase tracking-widest text-slate-600 px-3 mb-1.5">{group}</div>
              <div className="space-y-0.5">
                {items.map(([k, l, I]) => (
                  <button key={k} onClick={() => setSection(k)} data-testid={`admin-nav-${k}`}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-mono uppercase tracking-wider transition-colors ${section === k ? "bg-cyan-400/10 text-cyan-300 border border-cyan-400/20" : "text-slate-400 hover:bg-white/5 hover:text-slate-200"}`}>
                    <I size={14} /> {l}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      <main className="flex-1 p-6 lg:p-10 overflow-x-hidden">
        {section === "dashboard" && stats && <Dashboard stats={stats} />}
        {section === "home" && <HomeEditor />}
        {section === "chrome" && <ChromeEditor />}
        {section === "media" && <MediaLibrary />}
        {section === "products" && <ProductsManager />}
        {section === "guides" && <GuideEditor />}
        {section === "vrlab" && <VRLab />}
        {section === "invoicing" && <InvoiceAdmin />}
        {section === "orders" && <OrdersManager />}
        {section === "users" && <UsersManager />}
        {section === "prog_overview" && <ProgramsAdmin mode="overview" />}
        {section === "prog_users" && <ProgramsAdmin mode="users" />}
        {section === "prog_audit" && <ProgramsAdmin mode="audit" />}
        {CRUD.has(section) && <CrudManager collection={section} />}
        {READONLY.includes(section) && <ReadOnlyTable section={section} />}
        {section === "settings" && <SettingsView />}
      </main>
    </div>
  );
}
